import { FC, useState, useMemo } from 'react';
import { Grid, Typography, Alert, Box, TextField, Chip, CircularProgress, Card, CardContent } from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianAttendanceHook } from '../../../../hooks/useGuardianAcademicHook';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CheckCircle, Cancel, Schedule } from '@mui/icons-material';

const GuardianAttendanceView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const { data: attendanceData, isLoading, error } = useGuardianAttendanceHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
    }
  );

  const attendance = useMemo(() => attendanceData?.data || [], [attendanceData]);
  const totalCount = attendanceData?.count ?? 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'absent':
        return <Cancel sx={{ color: 'error.main' }} />;
      case 'late':
        return <Schedule sx={{ color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'present':
        return 'success';
      case 'absent':
        return 'error';
      case 'late':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'date',
      headerName: translateText('common.date'),
      width: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      field: 'status',
      headerName: translateText('common.status'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as string;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {getStatusIcon(status)}
            <Chip
              label={translateText(`guardian.academics.attendance.status.${status}`)}
              size="small"
              color={getStatusColor(status)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'period',
      headerName: translateText('guardian.academics.attendance.period'),
      width: 150,
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return attendance.map((record, index) => ({
      id: record.date || `attendance-${index}`,
      date: record.date,
      status: record.status,
      period: record.period,
    }));
  }, [attendance]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const summary = useMemo(() => {
    const present = attendance.filter((a) => a.status === 'present').length;
    const absent = attendance.filter((a) => a.status === 'absent').length;
    const late = attendance.filter((a) => a.status === 'late').length;
    const total = attendance.length;
    const percentage = total > 0 ? ((present / total) * 100).toFixed(1) : '0';
    return { present, absent, late, total, percentage };
  }, [attendance]);

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-attendance-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-attendance-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-attendance-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-attendance-container">
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {translateText('guardian.academics.attendance.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.academics.attendance.summary.present')}
            </Typography>
            <Typography variant="h4" color="success.main">
              {summary.present}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.academics.attendance.summary.absent')}
            </Typography>
            <Typography variant="h4" color="error.main">
              {summary.absent}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              {translateText('guardian.academics.attendance.summary.percentage')}
            </Typography>
            <Typography variant="h4" color="primary.main">
              {summary.percentage}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="guardian-attendance-filters"
        >
          <TextField
            label={translateText('guardian.academics.assessments.filters.fromDate')}
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-attendance-from-date-filter"
          />
          <TextField
            label={translateText('guardian.academics.assessments.filters.toDate')}
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            size="small"
            data-testid="guardian-attendance-to-date-filter"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="guardian-attendance-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianAttendanceView;
